import React, { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L, { LatLngExpression, LatLngBoundsExpression } from "leaflet";
import { useHydrantyQuery } from "../api/JsdhNotifikaceService/service/HydrantyMutations";
import type { HydrantFeature } from "../api/JsdhNotifikaceService/generated";

const markerIcon = new L.Icon({
    iconUrl: "/maps/maker.png",
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [1, -34],
});

const hydrantIcon = new L.Icon({
    iconUrl: "/maps/hydrant.png",
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
});

const stationIcon = new L.DivIcon({
    html: `<svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <circle cx="18" cy="18" r="16" fill="#d32f2f" stroke="white" stroke-width="3"/>
        <text x="18" y="24" text-anchor="middle" fill="white" font-size="20" font-weight="bold">H</text>
    </svg>`,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
});

interface Hydrant {
    lat: number;
    lon: number;
    name?: string;
    type?: string;
    status?: string;
}

export function parseGpsString(gps: string): [number, number] | null {
    if (!gps) return null;
    const parts = gps.split(",");
    if (parts.length !== 2) return null;

    const latPart = parts[0].trim();
    const lngPart = parts[1].trim();

    const latMatch = latPart.match(/([\d.+-]+)\s*([NnSs])?/);
    const lngMatch = lngPart.match(/([\d.+-]+)\s*([EeWw])?/);

    if (!latMatch || !lngMatch) return null;

    let lat = parseFloat(latMatch[1]);
    let lng = parseFloat(lngMatch[1]);

    if (latMatch[2]?.toUpperCase() === "S") lat = -lat;
    if (lngMatch[2]?.toUpperCase() === "W") lng = -lng;

    return [lat, lng];
}

interface MapViewProps {
    gps: string;
    hasCisloPopisne?: boolean;
    height?: number | string;
    showHyndrant?: boolean;
    zoomLevel?: number;
    routeCoordinates?: [number, number][];
    stationGps?: string;
    fitToBounds?: boolean;
}

const ZoomSwitcher: React.FC<{ zoom: number; position: LatLngExpression }> = ({ zoom, position }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(position, zoom, { animate: true });
    }, [zoom, position, map]);
    return null;
};

// Padding pro fitBounds — kompenzuje časovkový overlay v top-right rohu (~120×56 px),
// aby se start trasy nikdy nedostal do pravého horního rohu.
const ROUTE_FIT_PADDING = {
    paddingTopLeft: [30, 90] as [number, number],
    paddingBottomRight: [140, 30] as [number, number],
};

// Container resize (např. layout misto-trasa kdy doběhne async routeData a levá mapa se zmenší
// z 100 % na 50 %) — bez invalidateSize() drží Leaflet stale _size a marker se vykreslí mimo střed.
// Voláme ho v obou view komponentách (Center i Bounds), aby resize fungoval bez ohledu na mód.
const useContainerResize = (onResize: () => void) => {
    const map = useMap();
    const onResizeRef = useRef(onResize);
    useEffect(() => { onResizeRef.current = onResize; }, [onResize]);
    useEffect(() => {
        const observer = new ResizeObserver(() => {
            map.invalidateSize({ animate: false });
            onResizeRef.current();
        });
        observer.observe(map.getContainer());
        return () => observer.disconnect();
    }, [map]);
};

const CenterUpdater: React.FC<{ position: LatLngExpression; zoom: number }> = ({ position, zoom }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(position, zoom, { animate: true });
    }, [position, zoom, map]);
    useContainerResize(() => map.setView(position, zoom, { animate: false }));
    return null;
};

const BoundsFitter: React.FC<{ bounds: LatLngBoundsExpression }> = ({ bounds }) => {
    const map = useMap();
    useEffect(() => {
        map.fitBounds(bounds, { ...ROUTE_FIT_PADDING, animate: true });
    }, [bounds, map]);
    useContainerResize(() => map.fitBounds(bounds, { ...ROUTE_FIT_PADDING, animate: false }));
    return null;
};

// Convert GeoJSON feature to Hydrant object
const featureToHydrant = (feature: HydrantFeature): Hydrant => {
    // GeoJSON uses [lon, lat], we need [lat, lon] for Leaflet
    const [lon, lat] = feature.geometry.coordinates;
    return {
        lat,
        lon,
        name: feature.properties?.name,
        type: feature.properties?.type,
        status: feature.properties?.status,
    };
};

// Get status color for popup
const getStatusColor = (status?: string): string => {
    const normalized = status?.toLowerCase();
    switch (normalized) {
        case 'funkcni':
        case 'funkční':
            return '#4caf50'; // green
        case 'nefunkcni':
        case 'nefunkční':
            return '#f44336'; // red
        default:
            return '#ff9800'; // orange for unknown
    }
};

// Get type label for popup
const getTypeLabel = (type?: string): string => {
    const normalized = type?.toLowerCase();
    switch (normalized) {
        case 'nadzemni':
        case 'nadzemní':
            return 'Nadzemni';
        case 'podzemni':
        case 'podzemní':
            return 'Podzemni';
        default:
            return type || 'Neznamy';
    }
};

// Get status label
const getStatusLabel = (status?: string): string => {
    const normalized = status?.toLowerCase();
    switch (normalized) {
        case 'funkcni':
        case 'funkční':
            return 'Funkcni';
        case 'nefunkcni':
        case 'nefunkční':
            return 'Nefunkcni';
        default:
            return status || 'Neznamy';
    }
};

const MapView: React.FC<MapViewProps> = ({
    gps,
    hasCisloPopisne = false,
    height = 500,
    showHyndrant = false,
    zoomLevel,
    routeCoordinates,
    stationGps,
    fitToBounds = false,
}) => {
    const coords = useMemo(() => parseGpsString(gps), [gps]);
    const stationCoords = useMemo(() => stationGps ? parseGpsString(stationGps) : null, [stationGps]);

    // Only fetch hydrants if they should be shown
    const { data: hydrantyGeoJSON } = useHydrantyQuery(showHyndrant);

    // Convert GeoJSON features to Hydrant objects
    const hydrants = useMemo<Hydrant[]>(() => {
        if (!hydrantyGeoJSON?.features) return [];
        return hydrantyGeoJSON.features.map(featureToHydrant);
    }, [hydrantyGeoJSON]);

    // Calculate bounds for route fitting
    const routeBounds = useMemo<LatLngBoundsExpression | null>(() => {
        if (!fitToBounds || !routeCoordinates || routeCoordinates.length === 0) return null;
        const allPoints: [number, number][] = [...routeCoordinates];
        if (coords) allPoints.push(coords);
        if (stationCoords) allPoints.push(stationCoords);
        if (allPoints.length < 2) return null;
        return allPoints.map(p => [p[0], p[1]] as [number, number]);
    }, [fitToBounds, routeCoordinates, coords, stationCoords]);

    if (!coords) return null;

    const position: LatLngExpression = coords;
    const [eventLat, eventLon] = coords;

    const activeZoom = zoomLevel ?? (hasCisloPopisne ? 15 : 14);

    function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number) {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) *
            Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    const nearby = hydrants.filter(h =>
        distanceKm(eventLat, eventLon, h.lat, h.lon) <= 0.15
    );

    return (
        <div style={{ height }}>
            <MapContainer
                center={position}
                zoom={activeZoom}
                style={{ height: "100%", width: "100%", borderRadius: 8 }}
                zoomControl={false}
                attributionControl={false}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                />
                <Marker position={position} icon={markerIcon}>
                    <Popup>
                        Zasah
                        <br />
                        {gps}
                    </Popup>
                </Marker>

                {/* Station marker */}
                {stationCoords && (
                    <Marker position={stationCoords} icon={stationIcon}>
                        <Popup>Zbrojnice</Popup>
                    </Marker>
                )}

                {/* Route polyline */}
                {routeCoordinates && routeCoordinates.length > 0 && (
                    <Polyline
                        positions={routeCoordinates}
                        pathOptions={{ color: '#1976d2', weight: 4, opacity: 0.8 }}
                    />
                )}

                {showHyndrant && activeZoom >= 16 &&
                    nearby.map((h, i) => (
                        <Marker key={i} position={[h.lat, h.lon]} icon={hydrantIcon}>
                            <Popup>
                                <div>
                                    <strong>{h.name || 'Hydrant'}</strong>
                                    <br />
                                    <span>Typ: {getTypeLabel(h.type)}</span>
                                    <br />
                                    <span style={{ color: getStatusColor(h.status) }}>
                                        Stav: {getStatusLabel(h.status)}
                                    </span>
                                </div>
                            </Popup>
                        </Marker>
                    ))}

                {/* Bounds fitting for route view, or standard center/zoom for place view */}
                {fitToBounds && routeBounds ? (
                    <BoundsFitter bounds={routeBounds} />
                ) : (
                    <>
                        <CenterUpdater position={position} zoom={activeZoom} />
                        {hasCisloPopisne && (
                            <ZoomSwitcher zoom={activeZoom} position={position} />
                        )}
                    </>
                )}
            </MapContainer>
        </div>
    );
};

export default MapView;
